import { Modal, Button, Loader, Checkbox, Group } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { JobArtifact, useUpdateArtifact, useCompileArtifact } from '../../api/artifacts';
import { useState, useRef, useLayoutEffect } from 'react';
import { Crepe } from '@milkdown/crepe';
// Import base styles first, then the dark theme
import '@milkdown/crepe/theme/common/style.css';
import '@milkdown/crepe/theme/nord-dark.css';

interface ResumeEditModalSingleProps {
  opened: boolean;
  onClose: () => void;
  resume: JobArtifact | null;
  jobId: string;
  onSuccess?: () => void;
}

export function ResumeEditModalSingle({
  opened,
  onClose,
  resume,
  jobId,
  onSuccess,
}: ResumeEditModalSingleProps) {
  const [markdown, setMarkdown] = useState('');
  const [createNewVersion, setCreateNewVersion] = useState(false);
  const crepeRef = useRef<HTMLDivElement>(null);
  const crepeInstanceRef = useRef<Crepe | null>(null);

  const updateMutation = useUpdateArtifact(resume?.id || '');
  const compileMutation = useCompileArtifact();

  // Initialize Crepe when ref becomes available
  useLayoutEffect(() => {
    // Don't do anything if modal closed
    if (!opened || !resume) {
      return;
    }

    // If ref not ready yet, set up a polling mechanism to wait for it
    if (!crepeRef.current) {
      const checkInterval = setInterval(() => {
        if (crepeRef.current && !crepeInstanceRef.current) {
          clearInterval(checkInterval);
          initializeCrepeEditor();
        }
      }, 50);

      return () => clearInterval(checkInterval);
    }

    // Ref is available, initialize immediately
    if (!crepeInstanceRef.current) {
      initializeCrepeEditor();
    }
  }, [opened, resume]);

  const initializeCrepeEditor = async () => {
    if (!crepeRef.current || !resume || crepeInstanceRef.current) {
      return;
    }

    try {
      const crepeInstance = new Crepe({
        root: crepeRef.current,
        defaultValue: resume.content_markdown,
      });

      crepeInstanceRef.current = crepeInstance;

      // Initialize Crepe
      await crepeInstance.create();

      // Load initial markdown into state
      const initialMarkdown = crepeInstance.getMarkdown();
      setMarkdown(initialMarkdown);

      // Set up change listener to track updates
      crepeInstance.on((listener) => {
        listener.markdownUpdated((_, markdown) => {
          setMarkdown(markdown);
        });
      });
    } catch (error) {
      console.error('Failed to initialize Crepe editor:', error);
    }
  };

  // Cleanup on unmount or modal close
  useLayoutEffect(() => {
    return () => {
      if (crepeInstanceRef.current) {
        (async () => {
          try {
            await crepeInstanceRef.current?.destroy();
          } catch (error) {
            console.error('Error destroying Crepe:', error);
          }
          crepeInstanceRef.current = null;
        })();
      }
    };
  }, []);

  const handleSave = async () => {
    if (!resume || !jobId) return;

    const markdownChanged = markdown !== resume.content_markdown;

    if (!markdownChanged) {
      onClose();
      return;
    }

    try {
      if (createNewVersion) {
        // Create a new version
        await compileMutation.mutateAsync({
          job_id: jobId,
          artifact_type: 'resume',
          name: `${resume.name}`,
          content_markdown: markdown,
          content_ids: resume.used_content_ids || [],
          stylesheet_id: resume.stylesheet_id,
          metadata: resume.metadata,
        });
      } else {
        // Update current version
        await updateMutation.mutateAsync({
          name: resume.name,
          content_markdown: markdownChanged ? markdown : undefined,
          stylesheet_id: resume.stylesheet_id,
          metadata: resume.metadata,
        });
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error saving resume:', error);
    }
  };

  const isLoading = updateMutation.isPending || compileMutation.isPending;
  const markdownChanged = markdown !== (resume?.content_markdown || '');

  if (!resume) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      fullScreen
      withCloseButton={false}
      styles={{
        content: {
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          height: '100vh',
        },
        body: {
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          flex: 1,
        },
      }}
    >
      {/* Fixed header with title and buttons - stays at top */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          padding: '1rem',
          borderBottom: '1px solid var(--mantine-color-gray-3)',
          backgroundColor: 'var(--mantine-color-dark-7)',
          zIndex: 10,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '1rem',
        }}
      >
        <h2 style={{ margin: 0, flex: 1, fontSize: '1.25rem', fontWeight: 600 }}>
          Edit: {resume.name} (v{resume.version}) - Markdown Editor
        </h2>
        <Group gap="sm">
          <Button variant="light" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            leftSection={isLoading ? <Loader size={14} /> : <IconCheck size={14} />}
            onClick={handleSave}
            loading={isLoading}
            disabled={isLoading || (!markdownChanged && !createNewVersion)}
          >
            {createNewVersion ? 'Create New Version' : 'Save Changes'}
          </Button>
        </Group>
      </div>

      {/* Scrollable content area */}
      <div
        style={{
          flex: 1,
          padding: '1rem',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        {/* Version control option */}
        {markdownChanged && (
          <Checkbox
            label="Create a new version (instead of updating current version)"
            checked={createNewVersion}
            onChange={(e) => setCreateNewVersion(e.currentTarget.checked)}
            description="If unchecked, changes will update the current version"
          />
        )}

        {/* Crepe Editor Container - takes remaining space */}
        <div
          ref={crepeRef}
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '0.5rem',
            overflow: 'hidden',
            border: '1px solid var(--mantine-color-gray-3)',
          }}
        />
      </div>
    </Modal>
  );
}
